import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminContentEditorFields,
  AdminPageHeader,
  appendYowanContentPreviewQuery,
  useAdminBlogAuthorOptions,
  useAdminContentMediaOptions,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminBlogDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanAdminBlog,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminBlogEditPage = () => {
  const content = yowanPageContent['/admin/content/blog/:blogId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { blogId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  } = useAdminContentMediaOptions('blog', currentRecord?.primary_image);
  const {
    authorOptions,
    authorOptionsLoading,
    authorOptionsError,
  } = useAdminBlogAuthorOptions(currentRecord?.author);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        title: '',
        slug: '',
        isPublished: false,
        publishedAt: '',
        excerpt: '',
        body: '',
        primary_image_id: '',
      },
  });

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const updated = await updateYowanAdminBlog(blogId, formValues);
      setSubmitMessage('保存しました。');
      if (updated?.id || blogId) {
        navigate(appendYowanContentPreviewQuery('/admin/content/blog', preservedDomainSearch));
      }
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanAdminBlogDetail(blogId)
      .then((record) => {
        if (isMounted) {
          setCurrentRecord(record);
          reset(record);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentRecord(null);
          setSubmitMessage('記事を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [blogId, reset]);

  if (!content) {
    return null;
  }

  if (!currentRecord) {
    return (
      <AdminBlogShell activeNavHref="/admin/content/blog" routeKey="/admin/content/blog/:blogId/edit">
        <AdminPageHeader
          title={content.header.title}
          paragraphs={content.header.paragraphs}
          actions={[
            <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/blog', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <p className="yowan-admin-form__message">{submitMessage || '記事が見つかりませんでした。'}</p>
        )}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref="/admin/content/blog" routeKey="/admin/content/blog/:blogId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/blog', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          currentRecord.publicHref ? (
            <Link
              key="public-preview"
              to={appendYowanContentPreviewQuery(currentRecord.publicHref, preservedDomainSearch)}
            >
              公開面
            </Link>
          ) : null,
        ].filter(Boolean)}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminContentEditorFields
          content={content}
          register={register}
          errors={errors}
          requireTitleAndSlug={false}
          slugReadOnly
          authorOptions={authorOptions}
          authorOptionsLoading={authorOptionsLoading}
          authorOptionsError={authorOptionsError}
          mediaAssetOptions={mediaAssetOptions}
          mediaAssetLoading={mediaAssetLoading}
          mediaAssetError={mediaAssetError}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>

        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminBlogEditPage;
